/**
 * Utility to join class names, filtering out falsy values
 * 
 * This is a lightweight alternative to the classnames/clsx packages,
 * providing type-safe class name concatenation.
 * 
 * @example
 * ```tsx
 * cx('button', isActive && 'button--active', className)
 * // => 'button button--active custom-class'
 * 
 * cx('tag', {
 *   'tag--disabled': disabled,
 *   'tag--active': active
 * })
 * // => 'tag tag--disabled tag--active' (if both are true)
 * ```
 */
export function cx(...classes: (string | boolean | undefined | null | Record<string, boolean>)[]): string {
  return classes
    .filter(Boolean)
    .map(cls => {
      if (typeof cls === 'string') {
        return cls;
      }
      if (typeof cls === 'object' && cls !== null) {
        // Handle object syntax: { 'class-name': condition }
        return Object.entries(cls)
          .filter(([, value]) => value)
          .map(([key]) => key)
          .join(' ');
      }
      return '';
    })
    .filter(Boolean)
    .join(' ');
}

/**
 * Build BEM-style class names with modifiers
 * 
 * @example
 * ```tsx
 * bemClass('button', ['primary', 'large'])
 * // => 'button button--primary button--large'
 * 
 * bemClass('tag', ['active'], 'custom-tag')
 * // => 'tag tag--active custom-tag'
 * ```
 */
export function bemClass(
  block: string,
  modifiers?: (string | boolean | undefined | null)[],
  additionalClasses?: string
): string {
  const base = block;
  const mods = modifiers
    ?.filter((mod): mod is string => typeof mod === 'string' && Boolean(mod))
    .map(mod => `${block}--${mod}`)
    .join(' ') || '';
  
  return cx(base, mods, additionalClasses);
}

/**
 * Build class name for a BEM element
 * 
 * @example
 * ```tsx
 * bemElement('button', 'icon')
 * // => 'button__icon'
 * 
 * bemElement('button', 'icon', ['large', 'rotated'])
 * // => 'button__icon button__icon--large button__icon--rotated'
 * ```
 */
export function bemElement(
  block: string,
  element: string,
  modifiers?: (string | boolean | undefined | null)[]
): string {
  const base = `${block}__${element}`;
  const mods = modifiers
    ?.filter((mod): mod is string => typeof mod === 'string' && Boolean(mod))
    .map(mod => `${base}--${mod}`)
    .join(' ') || '';
  
  return cx(base, mods);
}

/**
 * Generate state-based class names
 * 
 * @example
 * ```tsx
 * stateClasses('button', {
 *   hovered: isHovered,
 *   pressed: isPressed,
 *   disabled: disabled
 * })
 * // => 'button--hovered button--pressed button--disabled'
 * ```
 */
export function stateClasses(
  block: string,
  states: Record<string, boolean | undefined>
): string {
  return Object.entries(states)
    .filter(([, active]) => active)
    .map(([state]) => `${block}--${state}`)
    .join(' ');
}

